import { AppIcon } from '@/components/ui/app-icon';
import { useClerk, useOrganization, useUser } from '@clerk/react';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CLEANLIST_TEAM_CALL_URL,
  OnboardingBookingStep,
} from '@/components/onboarding-booking-step.component';
import { Button } from '@/components/ui/button';
import {
  trackIntercomEvent,
  updateIntercomUser,
} from '@/hooks/use-intercom.hook';
import { bestEffortAnalytics } from '@/lib/best-effort-analytics';
import { iconCheck, iconCircleCheckSolid, iconGift } from '@/lib/icons';
import { cn } from '@/lib/utils';

import { useBookingCalendar } from './use-booking-calendar.hook';
import { BRAND, brandAsset } from '@/lib/brand';

/** Same beat as the survey's old booking step: the call lands first, then the
 *  way out appears. The product is never gated behind the call. */
const SKIP_REVEAL_MS = 2000;

interface Host {
  name: string;
  firstName: string;
  /** Shown under the name; omitted when there is no title to show. */
  title?: string;
  photo: string;
}

/** Who answers each calendar. The calendar comes from useBookingCalendar
 *  (the setup run's routing, else the seat band), so this only puts a face
 *  to it. */
const SAMEER: Host = {
  name: 'Sameer Mian',
  firstName: 'Sameer',
  photo: '/team/sameer.jpg',
};
const LEV: Host = {
  name: 'Lev Adamyan',
  firstName: 'Lev',
  title: `CEO of ${BRAND.fullName}`,
  photo: '/team/lev.jpg',
};
const hostFor = (calendarUrl: string) =>
  calendarUrl === CLEANLIST_TEAM_CALL_URL ? LEV : SAMEER;

const AGENDA = [
  'Build your first list with you, from search to enriched contacts',
  'Set up agents that research and qualify your accounts',
  'Connect your CRM and get your team in',
];

function HostPhoto({ host, size }: { host: Host; size: number }) {
  return (
    <img
      src={host.photo}
      alt={host.name}
      width={size}
      height={size}
      className='shrink-0 rounded-full object-cover ring-2 ring-background'
      style={{ width: size, height: size }}
    />
  );
}

/**
 * The onboarding call, shown after the survey and the trial decision: back
 * from Stripe with a card, after choosing Free, or straight after the survey
 * when there is no trial offer. Full screen like the trial page, led by the
 * person who will actually be on the call, with the calendar embedded.
 */
export function PostTrialBooking({
  onTrial,
  onDone,
}: {
  /** A trial is running, as opposed to staying on Free. */
  onTrial: boolean;
  /** Booked, skipped or fell back to the Calendly tab: open the app. */
  onDone: () => void;
}) {
  const { user } = useUser();
  const { organization } = useOrganization();
  const clerk = useClerk();
  const posthog = usePostHog();
  const [booked, setBooked] = useState(false);
  const [skipVisible, setSkipVisible] = useState(false);
  const viewedRef = useRef(false);
  const email = user?.primaryEmailAddress?.emailAddress;
  // Onboarding saved its answers on the user.
  const answer = (key: string) => {
    const value = user?.unsafeMetadata?.[key];
    return typeof value === 'string' ? value : undefined;
  };
  const seats = answer('seats');
  const calendarUrl = useBookingCalendar(seats, email);
  const host = hostFor(calendarUrl);
  const surface = onTrial ? 'post_trial' : 'post_free';
  const firstName = user?.firstName?.trim();

  useEffect(() => {
    const id = setTimeout(() => setSkipVisible(true), SKIP_REVEAL_MS);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    bestEffortAnalytics(() =>
      posthog?.capture('onboarding_call_offer_viewed', {
        surface,
        host: host.firstName,
      })
    );
  }, [posthog, surface, host.firstName]);

  /** Calendly's `event_scheduled`, not a click: the signal the credit grant and
   *  the booking alerts already run on. */
  const handleBooked = useCallback(() => {
    setBooked(true);
    bestEffortAnalytics(() =>
      posthog?.capture('onboarding_call_booked', {
        user_id: user?.id,
        email,
        organization_id: organization?.id ?? null,
        seats: seats ?? null,
        surface,
      })
    );
    trackIntercomEvent('booked-onboarding-call');
    updateIntercomUser({ onboarding_call_booked: true });
    void user?.update({
      unsafeMetadata: { ...user.unsafeMetadata, onboarding_call_booked: true },
    });
  }, [posthog, user, email, seats, surface, organization?.id]);

  const handleFallbackBook = useCallback(() => {
    bestEffortAnalytics(() =>
      posthog?.capture('onboarding_call_booking_opened', {
        user_id: user?.id,
        email,
        organization_id: organization?.id ?? null,
        reason: 'embed_unavailable',
        surface,
      })
    );
    trackIntercomEvent('opened-onboarding-booking');
    updateIntercomUser({ onboarding_call_booking_opened: true });
    onDone();
  }, [posthog, user, email, surface, organization?.id, onDone]);

  const handleSkip = () => {
    bestEffortAnalytics(() =>
      posthog?.capture('onboarding_call_skipped', {
        user_id: user?.id,
        surface,
      })
    );
    onDone();
  };

  return (
    <main
      className='relative flex min-h-dvh flex-col bg-background lg:flex-row'
      aria-labelledby='post-trial-booking-title'
    >
      <section className='relative flex flex-col overflow-hidden px-6 py-6 sm:px-10 lg:w-[44%] lg:max-w-[600px] lg:px-14'>
        {/* Same warm wash as onboarding, so this reads as its last page. */}
        <div
          aria-hidden='true'
          className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(235,180,145,0.28)_0%,transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(160,180,230,0.25)_0%,transparent_60%)] dark:bg-[radial-gradient(ellipse_at_top_left,rgba(235,180,145,0.08)_0%,transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(160,180,230,0.07)_0%,transparent_60%)]'
        />
        <header className='relative flex items-center justify-between gap-3'>
          <img
            src={brandAsset('/logo-dark.png')}
            alt={BRAND.name}
            className='h-8 w-auto dark:hidden'
          />
          <img
            src={brandAsset('/logo-light.png')}
            alt={BRAND.name}
            className='hidden h-8 w-auto dark:block'
          />
          <Button
            variant='ghost'
            size='sm'
            className='text-xs text-muted-foreground'
            onClick={() => void clerk.signOut()}
          >
            Sign out
          </Button>
        </header>

        <div className='relative flex flex-1 items-center py-10'>
          <div className='w-full max-w-[440px]'>
            <span
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold',
                onTrial
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  : 'bg-foreground/5 text-muted-foreground'
              )}
            >
              <AppIcon icon={iconCircleCheckSolid} style={{ fontSize: 12 }} />
              {onTrial
                ? 'Your 14-day Enterprise trial is live'
                : "You're on the Free plan"}
            </span>

            <h1
              id='post-trial-booking-title'
              className='mt-6 text-[34px] leading-[1.1] font-semibold tracking-[-0.03em] text-balance text-foreground sm:text-[40px]'
            >
              Set up {BRAND.name} with {host.firstName} in 30 minutes
            </h1>

            {/* The person on the call, in their own words. */}
            <figure className='mt-7 rounded-2xl border border-border/60 bg-background/80 p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.12)] backdrop-blur-sm'>
              <blockquote className='text-[15px] leading-7 text-foreground'>
                {firstName ? `Hi ${firstName}, ` : 'Hi, '}
                I&rsquo;d love to get you set up properly. We&rsquo;ll build
                your first list together, live, and I&rsquo;ll answer anything
                you&rsquo;re unsure about. Bring a few accounts you want to
                reach.
              </blockquote>
              <figcaption className='mt-4 flex items-center gap-3'>
                <HostPhoto host={host} size={44} />
                <div className='min-w-0'>
                  <p className='text-sm font-semibold text-foreground'>
                    {host.name}
                  </p>
                  {host.title && (
                    <p className='text-[13px] text-muted-foreground'>
                      {host.title}
                    </p>
                  )}
                </div>
              </figcaption>
            </figure>

            <ul className='mt-7 space-y-3'>
              {AGENDA.map((line) => (
                <li
                  key={line}
                  className='flex gap-3 text-[15px] leading-6 text-foreground'
                >
                  <span className='mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
                    <AppIcon icon={iconCheck} style={{ fontSize: 10 }} />
                  </span>
                  {line}
                </li>
              ))}
            </ul>

            <p className='mt-7 inline-flex items-center gap-2.5 rounded-full bg-amber-500/10 px-3.5 py-1.5 text-[13px] text-amber-800 dark:text-amber-300'>
              <AppIcon icon={iconGift} style={{ fontSize: 13 }} />
              <span>
                <span className='font-semibold'>500 bonus credits</span> after
                the call
              </span>
            </p>

            <div>
              <button
                type='button'
                onClick={handleSkip}
                hidden={booked}
                aria-hidden={!skipVisible}
                tabIndex={skipVisible ? undefined : -1}
                className={cn(
                  'mt-8 text-sm font-medium text-muted-foreground underline-offset-4 transition-opacity duration-300 hover:text-foreground hover:underline',
                  skipVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
                )}
              >
                Skip for now, take me to {BRAND.name}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className='flex flex-1 flex-col items-center justify-center bg-muted/40 px-4 pb-10 sm:px-8 lg:border-l lg:border-border/60 lg:px-10 lg:py-10 dark:bg-white/[0.02]'>
        <div className='w-full max-w-[720px]'>
          {!booked && (
            <div className='mb-4 flex items-center gap-3 px-1'>
              <HostPhoto host={host} size={36} />
              <div className='min-w-0'>
                <p className='text-sm font-semibold text-foreground'>
                  30-minute onboarding call with {host.firstName}
                </p>
                <p className='text-[13px] text-muted-foreground'>
                  Free · pick any open time below
                </p>
              </div>
            </div>
          )}
          <OnboardingBookingStep
            onBooked={handleBooked}
            onContinue={onDone}
            onFallbackBook={handleFallbackBook}
            booked={booked}
            calendarUrl={calendarUrl}
            seats={seats}
            referralSource={answer('referral_source')}
          />
        </div>
      </section>
    </main>
  );
}
